import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, setError } from '../app/redux/slices/authSlice';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';
import LanguageSwitcher from '../app/components/LanguageSwitcher';

export default function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const email = searchParams.get('email') || '';
  const { error, loading } = useSelector((state: RootState) => state.auth);

  // Password validation - at least 8 characters
  useEffect(() => {
    if (formData.newPassword) {
      const reg = /.{8,}$/;
      if (!reg.test(formData.newPassword)) {
        setPasswordError(true);
      } else {
        setPasswordError(false);
      }
    } else {
      setPasswordError(false);
    }
  }, [formData.newPassword]);

  // Password confirmation validation
  useEffect(() => {
    if (formData.confirmPassword && formData.newPassword) {
      if (formData.confirmPassword !== formData.newPassword) {
        setPasswordMismatch(true);
      } else {
        setPasswordMismatch(false);
      }
    } else {
      setPasswordMismatch(false);
    }
  }, [formData.confirmPassword, formData.newPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code || !formData.newPassword || !formData.confirmPassword) {
      dispatch(setError(t('common:error')));
      return;
    }

    if (passwordError) {
      dispatch(setError(t('auth:passwordMinLength')));
      return;
    }

    if (passwordMismatch) {
      dispatch(setError(t('auth:passwordsDoNotMatch')));
      return;
    }

    if (!email) {
      dispatch(setError(t('common:error')));
      return;
    }

    try {
      await dispatch(resetPassword({
        email: email,
        code: formData.code,
        newpassword: formData.newPassword
      })).unwrap();
      setSuccess(true);
    } catch (error) {
      logger.error('Failed to reset password:', error);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('auth:resetPasswordTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-900">
              {t('auth:createNewPassword')}
            </p>
          </div>

          <div>
            <button
              onClick={goToLogin}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('auth:backToSignIn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:resetPasswordTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900">
            {t('auth:enterResetCode')}{' '}
            <span className="font-medium text-indigo-600">{email}</span>
          </p>
        </div>
        
        {/* Language Switcher */}
        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {t('common:error')}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => dispatch(setError(''))}
                      className="text-sm bg-red-50 text-red-800 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {t('auth:dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Verification Code */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-900">
                {t('auth:verificationCode')}
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center text-lg tracking-widest"
                placeholder={t('auth:enterVerificationCode')}
                value={formData.code}
                onChange={handleInputChange}
                maxLength={6}
              />
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-900">
                {t('auth:newPassword')}
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:enterPassword')}
                value={formData.newPassword}
                onChange={handleInputChange}
              />
              {passwordError && (
                <p className="mt-1 text-sm text-red-600">{t('auth:passwordMinLength')}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                {t('auth:confirmPasswordLabel')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:confirmPasswordPlaceholder')}
                value={formData.confirmPassword}
                onChange={handleInputChange}
              />
              {passwordMismatch && (
                <p className="mt-1 text-sm text-red-600">{t('auth:passwordsDoNotMatch')}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth:resettingPassword') : t('auth:resetButton')}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link 
              to="/forgot-password" 
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:backToLogin')}
            </Link>
            <br />
            <Link 
              to="/login" 
              className="font-medium text-gray-900 hover:text-gray-800 text-sm"
            >
              {t('auth:backToSignIn')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
